import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, Search, Plus, RefreshCw, Loader2, ChevronRight,
  AlertTriangle, CheckCircle2, XCircle, Target, Crosshair,
  Zap, Users, ArrowRight, Clock, Brain, Eye, Layers
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PHASE_CONFIG = {
  discovery: { label: 'Discovery', color: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800', icon: Search, step: 1 },
  prioritization: { label: 'Prioritization', color: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800', icon: Target, step: 2 },
  validation: { label: 'Validation', color: 'bg-purple-500', badge: 'bg-purple-100 text-purple-800', icon: Crosshair, step: 3 },
  mobilization: { label: 'Mobilization', color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', icon: Users, step: 4 },
  closed: { label: 'Closed', color: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, step: 5 },
};

const SEV_BADGE = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

function PhaseTimeline({ phases }) {
  const total = Object.values(phases).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="flex items-center gap-1" data-testid="ctem-phase-timeline">
      {Object.entries(PHASE_CONFIG).map(([key, cfg]) => {
        const count = phases[key] || 0;
        const pct = Math.max(count > 0 ? 8 : 2, (count / total) * 100);
        return (
          <div key={key} className="flex flex-col items-center flex-1 min-w-0">
            <div className={`w-full h-3 rounded-full ${cfg.color} transition-all`} style={{ opacity: count > 0 ? 1 : 0.2 }} />
            <div className="mt-2 text-center">
              <p className="text-xs font-medium text-slate-700">{cfg.label}</p>
              <p className="text-lg font-bold text-slate-900">{count}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExposureRow({ exposure, onAction }) {
  const phase = PHASE_CONFIG[exposure.phase] || PHASE_CONFIG.discovery;
  const PhaseIcon = phase.icon;
  return (
    <div className="flex items-center justify-between p-4 border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`exposure-row-${exposure.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-2 h-10 rounded-full flex-shrink-0 ${
          exposure.severity === 'critical' ? 'bg-red-500' :
          exposure.severity === 'high' ? 'bg-orange-500' :
          exposure.severity === 'medium' ? 'bg-amber-400' : 'bg-blue-400'
        }`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate">{exposure.title}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`text-[10px] ${SEV_BADGE[exposure.severity] || SEV_BADGE.medium}`}>{exposure.severity}</Badge>
            <span className="text-xs text-slate-400">{exposure.source}</span>
            {exposure.cve_ids?.length > 0 && (
              <span className="text-xs font-mono text-slate-500">{exposure.cve_ids[0]}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right mr-2">
          <p className="text-sm font-bold text-slate-900">{exposure.exposure_score}</p>
          <p className="text-[10px] text-slate-400">score</p>
        </div>
        <Badge className={phase.badge}>
          <PhaseIcon size={10} className="mr-1" />
          {phase.label}
        </Badge>
        {exposure.validation_status && (
          <Badge className={
            exposure.validation_status === 'exploitable' ? 'bg-red-100 text-red-800' :
            exposure.validation_status === 'not_exploitable' ? 'bg-emerald-100 text-emerald-800' :
            'bg-amber-100 text-amber-800'
          }>
            {exposure.validation_status === 'exploitable' ? 'Exploitable' :
             exposure.validation_status === 'not_exploitable' ? 'Safe' : 'Investigating'}
          </Badge>
        )}
        {/* Action buttons based on phase */}
        {exposure.phase === 'discovery' && (
          <Button variant="ghost" size="sm" onClick={() => onAction('prioritize', exposure.id)} data-testid={`prioritize-${exposure.id}`}>
            <ArrowRight size={14} />
          </Button>
        )}
        {exposure.phase === 'prioritization' && (
          <Button variant="ghost" size="sm" onClick={() => onAction('ai-validate', exposure.id)} data-testid={`validate-${exposure.id}`}>
            <Brain size={14} className="mr-1" /> AI
          </Button>
        )}
        {exposure.phase === 'validation' && (
          <Button variant="ghost" size="sm" onClick={() => onAction('mobilize', exposure.id)}>
            <Users size={14} />
          </Button>
        )}
        {exposure.phase === 'mobilization' && (
          <Button variant="ghost" size="sm" onClick={() => onAction('remediate', exposure.id)}>
            <CheckCircle2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CTEMPage() {
  const [dashboard, setDashboard] = useState(null);
  const [exposures, setExposures] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterPhase, setFilterPhase] = useState(null);
  const [showAddExposure, setShowAddExposure] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [newExposure, setNewExposure] = useState({ title: '', description: '', severity: 'medium', exposure_type: 'vulnerability' });
  const [newAsset, setNewAsset] = useState({ name: '', asset_type: 'server', criticality: 'medium', environment: 'production' });

  const token = localStorage.getItem('grc_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [dash, exp, ast] = await Promise.all([
        axios.get(`${API}/api/ctem/dashboard`, { headers }),
        axios.get(`${API}/api/ctem/exposures${filterPhase ? `?phase=${filterPhase}` : ''}`, { headers }),
        axios.get(`${API}/api/ctem/assets`, { headers }),
      ]);
      setDashboard(dash.data);
      setExposures(exp.data);
      setAssets(ast.data);
    } catch (err) {
      toast.error('Failed to load CTEM data');
    } finally {
      setLoading(false);
    }
  }, [filterPhase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await axios.post(`${API}/api/ctem/discover`, {}, { headers });
      toast.success(`Discovery complete: ${res.data.exposures_imported} new exposures found`);
      await fetchData();
    } catch { toast.error('Discovery failed'); }
    finally { setDiscovering(false); }
  };

  const handleAction = async (action, exposureId) => {
    try {
      if (action === 'prioritize') {
        await axios.put(`${API}/api/ctem/exposures/${exposureId}/phase?phase=prioritization`, {}, { headers });
        toast.success('Moved to Prioritization');
      } else if (action === 'ai-validate') {
        toast.info('Running AI breach simulation...');
        const res = await axios.post(`${API}/api/ctem/exposures/${exposureId}/ai-validate`, {}, { headers });
        const r = res.data.simulated_result || {};
        toast.success(`AI: ${r.exploitability || 'assessed'} — ${r.reasoning?.slice(0, 80) || ''}`);
      } else if (action === 'mobilize') {
        await axios.put(`${API}/api/ctem/exposures/${exposureId}/phase?phase=mobilization`, {}, { headers });
        toast.success('Moved to Mobilization');
      } else if (action === 'remediate') {
        await axios.put(`${API}/api/ctem/exposures/${exposureId}/remediate`, {}, { headers });
        toast.success('Marked as remediated');
      }
      await fetchData();
    } catch { toast.error('Action failed'); }
  };

  const handleAddExposure = async () => {
    if (!newExposure.title.trim()) return;
    try {
      await axios.post(`${API}/api/ctem/exposures`, newExposure, { headers });
      toast.success('Exposure added');
      setNewExposure({ title: '', description: '', severity: 'medium', exposure_type: 'vulnerability' });
      setShowAddExposure(false);
      await fetchData();
    } catch { toast.error('Failed to add exposure'); }
  };

  const handleAddAsset = async () => {
    if (!newAsset.name.trim()) return;
    try {
      await axios.post(`${API}/api/ctem/assets`, newAsset, { headers });
      toast.success('Asset added');
      setNewAsset({ name: '', asset_type: 'server', criticality: 'medium', environment: 'production' });
      setShowAddAsset(false);
      await fetchData();
    } catch { toast.error('Failed to add asset'); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      </DashboardLayout>
    );
  }

  const d = dashboard || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 px-1" data-testid="ctem-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers size={24} /> Continuous Threat Exposure Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">5-phase lifecycle: Scope, Discover, Prioritize, Validate, Mobilize</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDiscover} disabled={discovering} data-testid="ctem-discover-btn">
              {discovering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Run Discovery
            </Button>
            <Button onClick={() => setShowAddExposure(true)} data-testid="ctem-add-exposure-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Exposure
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Exposure Score', value: d.exposure_score || 0, icon: Zap, color: d.exposure_score > 7 ? 'text-red-600' : d.exposure_score > 4 ? 'text-amber-600' : 'text-emerald-600', bg: d.exposure_score > 7 ? 'bg-red-50' : d.exposure_score > 4 ? 'bg-amber-50' : 'bg-emerald-50' },
            { label: 'Total Exposures', value: d.total_exposures || 0, icon: Eye, color: 'text-slate-700', bg: 'bg-slate-50' },
            { label: 'Open', value: d.open || 0, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'In Progress', value: d.in_progress || 0, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Remediation Velocity', value: `${d.remediation_velocity || 0}/mo`, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={`border-0 ${card.bg}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} className={card.color} />
                    <span className="text-xs text-slate-500">{card.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Phase Timeline */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">CTEM Lifecycle Phases</CardTitle>
          </CardHeader>
          <CardContent>
            <PhaseTimeline phases={d.phases || {}} />
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-ctem-overview">Overview</TabsTrigger>
            <TabsTrigger value="exposures" data-testid="tab-ctem-exposures">Exposures ({d.total_exposures || 0})</TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-ctem-assets">Attack Surface ({d.total_assets || 0})</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Severity Breakdown */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Severity Distribution</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {['critical', 'high', 'medium', 'low'].map((sev) => {
                    const count = d.severities?.[sev] || 0;
                    const total = d.total_exposures || 1;
                    const colors = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-amber-400', low: 'bg-blue-400' };
                    return (
                      <div key={sev}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize text-slate-600">{sev}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${colors[sev]}`} style={{ width: `${(count / total) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Top Exposures */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Top 5 Exposures by Score</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(d.top_exposures || []).map((exp, i) => (
                      <div key={exp.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{exp.title}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge className={`text-[10px] ${SEV_BADGE[exp.severity]}`}>{exp.severity}</Badge>
                            <span className="text-[10px] text-slate-400">{exp.source}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${exp.exposure_score >= 10 ? 'text-red-600' : 'text-amber-600'}`}>
                            {exp.exposure_score}
                          </p>
                        </div>
                      </div>
                    ))}
                    {(!d.top_exposures || d.top_exposures.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">No exposures yet. Run Discovery to get started.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Exposures Tab */}
          <TabsContent value="exposures" className="mt-6">
            {/* Phase Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button variant={filterPhase === null ? 'default' : 'outline'} size="sm" onClick={() => setFilterPhase(null)}>All</Button>
              {Object.entries(PHASE_CONFIG).map(([key, cfg]) => (
                <Button key={key} variant={filterPhase === key ? 'default' : 'outline'} size="sm"
                  onClick={() => setFilterPhase(key)} data-testid={`filter-${key}`}>
                  {cfg.label} ({d.phases?.[key] || 0})
                </Button>
              ))}
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                {exposures.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {exposures.map((exp) => (
                      <ExposureRow key={exp.id} exposure={exp} onAction={handleAction} />
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-slate-500">
                    No exposures found. Run Discovery or add manually.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets" className="mt-6">
            <div className="flex justify-between mb-4">
              <p className="text-sm text-slate-500">{assets.length} assets in attack surface</p>
              <Button variant="outline" size="sm" onClick={() => setShowAddAsset(true)} data-testid="add-asset-btn">
                <Plus size={14} className="mr-1" /> Add Asset
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <Card key={asset.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`asset-card-${asset.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm text-slate-900">{asset.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{asset.asset_type} &middot; {asset.environment}</p>
                      </div>
                      <Badge className={`text-[10px] ${
                        asset.criticality === 'critical' ? 'bg-red-100 text-red-800' :
                        asset.criticality === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {asset.criticality}
                      </Badge>
                    </div>
                    {asset.tags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {asset.tags.map((tag) => (
                          <span key={tag} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 pt-2 border-t border-slate-100 text-xs text-slate-500">
                      {asset.exposure_count || 0} exposures linked
                    </div>
                  </CardContent>
                </Card>
              ))}
              {assets.length === 0 && (
                <div className="col-span-3 py-8 text-center text-sm text-slate-500">
                  No assets defined. Add your attack surface assets to begin scoping.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Exposure Modal */}
        {showAddExposure && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md" data-testid="add-exposure-modal">
              <CardHeader>
                <CardTitle className="text-lg">Add Exposure</CardTitle>
                <CardDescription>Manually report a new exposure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Title</label>
                  <Input placeholder="e.g., SQL Injection in login form" value={newExposure.title}
                    onChange={(e) => setNewExposure(p => ({...p, title: e.target.value}))} data-testid="exposure-title-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <textarea className="w-full border rounded-md p-2 text-sm" rows={3} placeholder="Describe the exposure..."
                    value={newExposure.description} onChange={(e) => setNewExposure(p => ({...p, description: e.target.value}))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Severity</label>
                    <select className="w-full border rounded-md p-2 text-sm mt-1" value={newExposure.severity}
                      onChange={(e) => setNewExposure(p => ({...p, severity: e.target.value}))}>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Type</label>
                    <select className="w-full border rounded-md p-2 text-sm mt-1" value={newExposure.exposure_type}
                      onChange={(e) => setNewExposure(p => ({...p, exposure_type: e.target.value}))}>
                      <option value="vulnerability">Vulnerability</option>
                      <option value="misconfiguration">Misconfiguration</option>
                      <option value="credential_leak">Credential Leak</option>
                      <option value="attack_path">Attack Path</option>
                      <option value="supply_chain">Supply Chain</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleAddExposure} data-testid="save-exposure-btn">Save Exposure</Button>
                  <Button variant="outline" onClick={() => setShowAddExposure(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Asset Modal */}
        {showAddAsset && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md" data-testid="add-asset-modal">
              <CardHeader>
                <CardTitle className="text-lg">Add Attack Surface Asset</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Asset Name</label>
                  <Input placeholder="e.g., Production DB Server" value={newAsset.name}
                    onChange={(e) => setNewAsset(p => ({...p, name: e.target.value}))} data-testid="asset-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Type</label>
                    <select className="w-full border rounded-md p-2 text-sm mt-1" value={newAsset.asset_type}
                      onChange={(e) => setNewAsset(p => ({...p, asset_type: e.target.value}))}>
                      {['server','application','database','network','cloud','endpoint','iot'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Criticality</label>
                    <select className="w-full border rounded-md p-2 text-sm mt-1" value={newAsset.criticality}
                      onChange={(e) => setNewAsset(p => ({...p, criticality: e.target.value}))}>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleAddAsset} data-testid="save-asset-btn">Save Asset</Button>
                  <Button variant="outline" onClick={() => setShowAddAsset(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
